import { useEffect } from 'react';

/**
 * Keeps the screen on while `active` and the page is visible — for reading a
 * recipe at the stove. The browser drops the lock whenever the tab is hidden,
 * so it is taken again on return. Where the API is missing or refuses, the
 * page simply behaves as before.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let released = false;

    const acquire = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const next = await navigator.wakeLock.request('screen');
        if (released) void next.release();
        else lock = next;
      } catch {
        // Denied (battery saver, no user gesture yet…) — nothing to do.
      }
    };
    const onVisibility = () => void acquire();

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}
